
import { useState, useMemo } from 'react';
import { countyAssessors } from '../../../mocks/county-assessors';

interface StateDirectoryProps {
  selectedState: string;
  searchQuery: string;
  isPremiumUser?: boolean;
}

export default function StateDirectory({ selectedState, searchQuery, isPremiumUser = false }: StateDirectoryProps) {
  const [selectedCounty, setSelectedCounty] = useState('');

  const stateData = useMemo(() => {
    if (!selectedState || !countyAssessors[selectedState as keyof typeof countyAssessors]) {
      return [];
    }
    
    const counties = countyAssessors[selectedState as keyof typeof countyAssessors];
    
    if (!searchQuery) return counties;
    
    return counties.filter(county => 
      county.county.toLowerCase().includes(searchQuery.toLowerCase()) ||
      county.assessorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      county.services.some(service => service.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [selectedState, searchQuery]);

  const stateName = {
    'CA': 'California',
    'TX': 'Texas', 
    'FL': 'Florida',
    'NY': 'New York'
  }[selectedState] || selectedState;

  const handleFloodMapClick = (county: string) => {
    if (!isPremiumUser) {
      window.location.href = '/pricing';
      return;
    }
    
    const url = `https://msc.fema.gov/portal/search?AddressQuery=${encodeURIComponent(county + ' County, ' + selectedState)}`;
    window.open(url, '_blank');
  };

  if (!selectedState) {
    return (
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-slate-800 rounded-2xl p-12 border border-slate-700">
            <i className="ri-map-pin-line text-6xl text-slate-500 mb-6"></i>
            <h3 className="text-2xl font-bold text-white mb-4">Select a State to Get Started</h3>
            <p className="text-slate-400 text-lg">
              Choose a state from the map above to view county assessor offices and appraisal districts
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (stateData.length === 0) {
    return (
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-4">{stateName} County Assessors</h2>
            <p className="text-slate-400">No results found for your search.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-slate-900 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">{stateName} County Assessors</h2>
          <p className="text-slate-400 text-lg">
            {stateData.length} county assessor office{stateData.length !== 1 ? 's' : ''} found
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {stateData.map((county, index) => (
            <div
              key={index}
              className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-emerald-500 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/10"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">{county.county}</h3>
                  <p className="text-slate-300 text-sm">{county.assessorName}</p>
                </div>
                <div className="w-12 h-12 flex items-center justify-center bg-emerald-600 rounded-lg">
                  <i className="ri-government-line text-white text-xl"></i>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center text-slate-400 text-sm">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center mr-3"></i>
                  <span>{county.phone}</span>
                </div>
                <div className="flex items-start text-slate-400 text-sm">
                  <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center mr-3 mt-0.5"></i>
                  <span>{county.address}</span>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-white font-semibold mb-3">Available Services</h4>
                <div className="flex flex-wrap gap-2">
                  {county.services.map((service, serviceIndex) => (
                    <span
                      key={serviceIndex}
                      className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-xs border border-slate-600"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <a
                  href={county.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-external-link-line mr-2"></i>
                  Visit Website
                </a>
                <button
                  onClick={() => setSelectedCounty(selectedCounty === county.county ? '' : county.county)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white rounded-lg transition-colors cursor-pointer"
                >
                  <i className={`${selectedCounty === county.county ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                </button>
              </div>

              {selectedCounty === county.county && (
                <div className="mt-4 p-4 bg-slate-700 rounded-lg border border-slate-600">
                  <h5 className="text-white font-semibold mb-3">Quick Access Links</h5>
                  <div className="space-y-2">
                    <a
                      href={`${county.website}/property-search`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-emerald-400 hover:text-emerald-300 text-sm cursor-pointer"
                    >
                      → Property Search
                    </a>
                    <a
                      href={`${county.website}/tax-records`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-emerald-400 hover:text-emerald-300 text-sm cursor-pointer"
                    >
                      → Tax Records
                    </a>
                    <a
                      href={`${county.website}/appeals`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-emerald-400 hover:text-emerald-300 text-sm cursor-pointer"
                    >
                      → Assessment Appeals
                    </a>
                    <button
                      onClick={() => handleFloodMapClick(county.county)}
                      className={`block text-sm cursor-pointer ${isPremiumUser ? 'text-blue-400 hover:text-blue-300' : 'text-slate-500'}`}
                    >
                      <i className="ri-flood-line mr-1"></i>
                      → FEMA Flood Maps
                      {!isPremiumUser && <span className="ml-1 text-xs text-amber-400">(Premium)</span>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Additional Resources */}
        <div className="mt-16 bg-slate-800 rounded-2xl p-8 border border-slate-700">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">Additional Resources</h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-600 rounded-full mx-auto mb-4">
                <i className="ri-file-text-line text-white text-2xl"></i>
              </div>
              <h4 className="text-white font-semibold mb-2">Property Records</h4>
              <p className="text-slate-400 text-sm">Access detailed property information, ownership history, and assessment records</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-violet-600 rounded-full mx-auto mb-4">
                <i className="ri-calculator-line text-white text-2xl"></i>
              </div>
              <h4 className="text-white font-semibold mb-2">Tax Calculations</h4>
              <p className="text-slate-400 text-sm">View current tax assessments, exemptions, and payment information</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-amber-600 rounded-full mx-auto mb-4">
                <i className="ri-scales-line text-white text-2xl"></i>
              </div>
              <h4 className="text-white font-semibold mb-2">Appeals Process</h4>
              <p className="text-slate-400 text-sm">Learn about assessment appeals, deadlines, and required documentation</p>
            </div>
            <div className="text-center relative">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                <i className="ri-flood-line text-white text-2xl"></i>
              </div>
              <h4 className="text-white font-semibold mb-2 flex items-center justify-center">
                FEMA Flood Data
                {!isPremiumUser && (
                  <span className="ml-2 px-2 py-1 bg-amber-600 text-white text-xs rounded-full">PREMIUM</span>
                )}
              </h4>
              <p className="text-slate-400 text-sm">
                {isPremiumUser 
                  ? "Official flood zone maps and risk assessments for property evaluation"
                  : "Premium feature - Official flood zone maps and risk assessments"
                }
              </p>
              {!isPremiumUser && (
                <button
                  onClick={() => window.location.href = '/pricing'}
                  className="mt-3 text-amber-400 hover:text-amber-300 text-sm cursor-pointer"
                >
                  Upgrade to Access →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
